import { useState, useEffect, memo, useCallback, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { LogOut, Menu, X, Zap, Shield, Settings, Package, Trophy, ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores'
import { useLoyaltyStore, useLoyaltyLevel, useShowMyLoyaltyFrame, useAchievements } from '../../stores/loyaltyStore'
import { AuthModal } from '../auth'
import { UserProfileModal } from '../profile'
import { DeliveryNotificationBell } from '../prizes/DeliveryNotificationCenter'
import { PrizeTrackingDashboard } from '../prizes/PrizeTrackingDashboard'
import PointsDisplay from '../loyalty/PointsDisplay'
import LoyaltyDashboard from '../loyalty/LoyaltyDashboard'
import Button from './Button'
import Avatar from './Avatar'

const Header = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [authMode, setAuthMode] = useState<'signin'>('signin')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  const [isPrizeTrackingOpen, setIsPrizeTrackingOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isLoyaltyModalOpen, setIsLoyaltyModalOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const { user, isAuthenticated, isAdmin, signOut } = useAuthStore()
  const loyaltyLevel = useLoyaltyLevel()
  const showMyLoyaltyFrame = useShowMyLoyaltyFrame()
  const achievements = useAchievements()
  const hasAnyUnlockedAchievements = useMemo(() => {
    return achievements.some(a => a.isUnlocked)
  }, [achievements])
  const { initializeLoyalty } = useLoyaltyStore()

  // Initialize loyalty when user logs in
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      initializeLoyalty(user.id)
    }
  }, [isAuthenticated, user?.id, initializeLoyalty])

  // Close auth modal when user successfully authenticates
  useEffect(() => {
    if (isAuthenticated && user && isAuthModalOpen) {
      setIsAuthModalOpen(false)
      setIsMobileMenuOpen(false) // Also close mobile menu if open
    }
  }, [isAuthenticated, user, isAuthModalOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Close loyalty modal with Escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isLoyaltyModalOpen) {
        setIsLoyaltyModalOpen(false)
      }
    }

    if (isLoyaltyModalOpen) {
      document.addEventListener('keydown', handleEscapeKey)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
      document.body.style.overflow = 'unset'
    }
  }, [isLoyaltyModalOpen])

  const handleSignOut = useCallback(async () => {
    await signOut()
    setIsMobileMenuOpen(false)
  }, [signOut])

  const openSignIn = useCallback(() => {
    setAuthMode('signin')
    setIsAuthModalOpen(true)
  }, [])

  const openLoyaltyModal = useCallback(() => {
    setIsLoyaltyModalOpen(true)
    setIsMobileMenuOpen(false) // Close mobile menu if open
  }, [])


  return (
    <>
      <motion.header
        className="sticky top-0 z-50 w-full border-b border-white/20 shadow-premium-sm"
        style={{
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)', // Safari support
        }}
        role="banner"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-50/30 via-white/20 to-secondary-50/30 backdrop-blur-sm" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Premium Logo */}
            <motion.div
              className="flex-shrink-0"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a href="/" className="flex items-center gap-3 group" aria-label="Wild Draw - Ir a página principal">
                <motion.div 
                  className="relative"
                  whileHover={{ rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300, damping: 10 }}
                >
                  <div className="w-12 h-12 bg-premium-primary rounded-2xl flex items-center justify-center shadow-premium-glow group-hover:shadow-premium-glow-lg transition-all duration-300">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <motion.div 
                    className="absolute -inset-1 bg-premium-primary rounded-2xl opacity-20 -z-10"
                    animate={{ 
                      scale: [1, 1.1, 1],
                      opacity: [0.2, 0.4, 0.2] 
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity, 
                      ease: "easeInOut" 
                    }}
                  />
                </motion.div>
                <div className="flex flex-col">
                  <span className="text-2xl font-display font-bold bg-premium-primary bg-clip-text text-transparent">
                    Wild Draw
                  </span>
                  <span className="text-xs text-gray-500 font-medium -mt-1">Premium Carpfishing Raffles</span>
                </div>
              </a>
            </motion.div>

            {/* Premium Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-3" aria-label="Navegación principal">
              {isAuthenticated && user ? (
                <div className="flex items-center space-x-3">
                  {/* Loyalty Points Display - Hidden on smaller screens */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="hidden lg:block"
                  >
                    <PointsDisplay
                      size="sm"
                      showAnimation={true}
                      className="cursor-pointer"
                      onClick={openLoyaltyModal}
                    />
                  </motion.div>

                  {/* Notification Bell */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <DeliveryNotificationBell
                      onClick={() => setIsPrizeTrackingOpen(true)}
                      className="bg-white/40 backdrop-blur-xl border border-white/20 shadow-premium-sm hover:bg-white/50 transition-all duration-300"
                    />
                  </motion.div>

                  {/* Admin Button - Only show if admin */}
                  {isAdmin && (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button
                        onClick={() => navigate('/admin')}
                        variant="glass"
                        size="sm"
                        leftIcon={<Shield className="w-4 h-4" />}
                        className="hidden xl:flex"
                      >
                        Admin
                      </Button>
                    </motion.div>
                  )}

                  {/* User Profile Dropdown */}
                  <div className="relative" ref={dropdownRef}>
                    <motion.button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center gap-2 px-3 py-2 bg-white/40 backdrop-blur-xl rounded-2xl border border-white/20 shadow-premium-sm hover:bg-white/50 transition-all duration-300"
                      whileHover={{ scale: 1.02, y: -1 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Avatar
                        src={user.photoURL}
                        name={user.displayName || user.email || 'Usuario'}
                        size="sm"
                        loyaltyLevel={loyaltyLevel}
                        showLoyaltyFrame={hasAnyUnlockedAchievements && showMyLoyaltyFrame}
                      />
                      <div className="hidden lg:flex flex-col text-left">
                        <span className="text-sm font-semibold text-gray-900">
                          {user.displayName || 'Usuario'}
                        </span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </motion.button>

                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {isDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          className="absolute right-0 mt-2 w-64 bg-white/90 backdrop-blur-xl rounded-2xl border border-white/20 shadow-premium-lg z-50"
                        >
                          <div className="p-2">
                            {/* Profile Header */}
                            <div className="px-3 py-2 mb-2">
                              <div className="flex items-center gap-3">
                                <Avatar
                                  src={user.photoURL}
                                  name={user.displayName || user.email || 'Usuario'}
                                  size="md"
                                  loyaltyLevel={loyaltyLevel}
                                  showLoyaltyFrame={hasAnyUnlockedAchievements && showMyLoyaltyFrame}
                                />
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900 text-sm">
                                    {user.displayName || 'Usuario'}
                                  </p>
                                  <p className="text-xs text-gray-600">{user.email}</p>
                                </div>
                              </div>
                            </div>

                            {/* Menu Items */}
                            <div className="space-y-1">
                              <button
                                onClick={() => {
                                  setIsProfileModalOpen(true)
                                  setIsDropdownOpen(false)
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-xl hover:bg-white/50 transition-colors"
                              >
                                <Settings className="w-4 h-4 text-gray-600" />
                                <span className="text-sm text-gray-900">Mi Perfil</span>
                              </button>

                              <button
                                onClick={() => {
                                  setIsPrizeTrackingOpen(true)
                                  setIsDropdownOpen(false)
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-xl hover:bg-white/50 transition-colors"
                              >
                                <Package className="w-4 h-4 text-gray-600" />
                                <span className="text-sm text-gray-900">Mis Entregas</span>
                              </button>

                              <button
                                onClick={() => {
                                  navigate('/my-raffles')
                                  setIsDropdownOpen(false)
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-xl hover:bg-white/50 transition-colors"
                              >
                                <Trophy className="w-4 h-4 text-gray-600" />
                                <span className="text-sm text-gray-900">Mis Sorteos</span>
                              </button>

                              {isAdmin && (
                                <button
                                  onClick={() => {
                                    navigate('/admin')
                                    setIsDropdownOpen(false)
                                  }}
                                  className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-xl hover:bg-white/50 transition-colors"
                                >
                                  <Shield className="w-4 h-4 text-gray-600" />
                                  <span className="text-sm text-gray-900">Panel Admin</span>
                                </button>
                              )}

                              <hr className="my-1 border-gray-200" />

                              <button
                                onClick={() => {
                                  handleSignOut()
                                  setIsDropdownOpen(false)
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-xl hover:bg-red-50 transition-colors text-red-600"
                              >
                                <LogOut className="w-4 h-4" />
                                <span className="text-sm">Cerrar Sesión</span>
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={openSignIn}
                      variant="glass"
                      size="sm"
                    >
                      Iniciar Sesión
                    </Button>
                  </motion.div>
                </div>
              )}
            </nav>

            {/* Premium Mobile menu button */}
            <nav className="md:hidden" aria-label="Navegación móvil">
              <motion.button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-3 rounded-2xl bg-white/40 backdrop-blur-xl border border-white/20 text-gray-700 hover:text-gray-900 hover:bg-white/60 transition-all duration-300 shadow-premium-sm"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.95 }}
                aria-label={isMobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <motion.div
                  animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {isMobileMenuOpen ? (
                    <X className="block h-5 w-5" />
                  ) : (
                    <Menu className="block h-5 w-5" />
                  )}
                </motion.div>
              </motion.button>
            </nav>
          </div>

          {/* Premium Mobile menu */}
          <motion.div
            id="mobile-menu"
            className={`md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}
            role="navigation"
            aria-label="Menú de navegación móvil"
            initial={{ opacity: 0, height: 0, y: -20 }}
            animate={{ 
              opacity: isMobileMenuOpen ? 1 : 0, 
              height: isMobileMenuOpen ? 'auto' : 0,
              y: isMobileMenuOpen ? 0 : -20
            }}
            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div
              className="px-4 pt-4 pb-6 space-y-4 border-t border-white/20 mt-1"
              style={{
                background: 'rgba(255, 255, 255, 0.90)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)', // Safari support
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent" />
              <div className="relative z-10">
                {isAuthenticated && user ? (
                  <div className="space-y-4">
                    <motion.button
                      onClick={() => {
                        setIsProfileModalOpen(true)
                        setIsMobileMenuOpen(false)
                      }}
                      className="flex items-center gap-4 px-4 py-3 bg-white/40 backdrop-blur-xl rounded-2xl border border-white/20 shadow-premium-sm hover:bg-white/50 transition-all duration-300 w-full text-left"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Avatar
                        src={user.photoURL}
                        name={user.displayName || user.email || 'Usuario'}
                        size="lg"
                        loyaltyLevel={loyaltyLevel}
                        showLoyaltyFrame={hasAnyUnlockedAchievements && showMyLoyaltyFrame}
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {user.displayName || 'Usuario'}
                        </p>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        <p className="text-xs text-blue-600 mt-1">Toca para ver perfil</p>
                      </div>
                      <Settings className="w-5 h-5 text-gray-400" />
                    </motion.button>

                    {/* Mobile Points Display */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 }}
                      className="flex justify-center"
                    >
                      <PointsDisplay
                        size="md"
                        showAnimation={true}
                        className="cursor-pointer"
                        onClick={openLoyaltyModal}
                      />
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <Button
                        onClick={() => {
                          setIsPrizeTrackingOpen(true)
                          setIsMobileMenuOpen(false)
                        }}
                        variant="glass"
                        size="sm"
                        className="w-full justify-start"
                        leftIcon={<Package className="w-4 h-4" />}
                      >
                        Mis Entregas
                      </Button>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.25 }}
                    >
                      <Button
                        onClick={() => {
                          navigate('/my-raffles')
                          setIsMobileMenuOpen(false)
                        }}
                        variant="glass"
                        size="sm"
                        className="w-full justify-start"
                        leftIcon={<Trophy className="w-4 h-4" />}
                      >
                        Mis Sorteos
                      </Button>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <Button
                        onClick={() => {
                          setIsProfileModalOpen(true)
                          setIsMobileMenuOpen(false)
                        }}
                        variant="glass"
                        size="sm"
                        className="w-full justify-start"
                        leftIcon={<Settings className="w-4 h-4" />}
                      >
                        Mi Perfil
                      </Button>
                    </motion.div>

                    {isAdmin && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.35 }}
                      >
                        <Button
                          onClick={() => {
                            navigate('/admin')
                            setIsMobileMenuOpen(false)
                          }}
                          variant="glass"
                          size="sm"
                          className="w-full justify-start"
                          leftIcon={<Shield className="w-4 h-4" />}
                        >
                          Panel Admin
                        </Button>
                      </motion.div>
                    )}

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <Button
                        onClick={handleSignOut}
                        variant="glass"
                        size="sm"
                        className="w-full justify-start"
                        leftIcon={<LogOut className="w-4 h-4" />}
                      >
                        Cerrar Sesión
                      </Button>
                    </motion.div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <Button
                        onClick={openSignIn}
                        variant="glass"
                        size="sm"
                        className="w-full justify-start"
                      >
                        Iniciar Sesión
                      </Button>
                    </motion.div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.header>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultMode={authMode}
      />

      <UserProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      {/* Prize Tracking Dashboard Modal */}
      {isPrizeTrackingOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="w-full h-full sm:h-auto sm:max-w-6xl sm:max-h-[95vh] bg-white sm:rounded-2xl shadow-xl overflow-hidden flex flex-col">
            <div className="flex-1 overflow-y-auto">
              <PrizeTrackingDashboard
                onClose={() => setIsPrizeTrackingOpen(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Loyalty Dashboard Modal */}
      {isLoyaltyModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setIsLoyaltyModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="loyalty-modal-title"
          aria-describedby="loyalty-modal-description"
        >
          <div
            className="w-full h-full sm:h-auto sm:max-w-2xl lg:max-w-4xl sm:max-h-[95vh] bg-white shadow-xl relative overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsLoyaltyModalOpen(false)}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 z-20 p-1.5 sm:p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors duration-200"
              aria-label="Cerrar panel de lealtad"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
            </button>

            {/* Screen reader only title and description */}
            <h2 id="loyalty-modal-title" className="sr-only">Panel de Lealtad Wild Draw</h2>
            <p id="loyalty-modal-description" className="sr-only">
              Dashboard completo de tu progreso de lealtad, puntos acumulados y recompensas disponibles
            </p>

            <div className="flex-1 overflow-y-auto">
              <LoyaltyDashboard
                embedded={true}
                onClose={() => setIsLoyaltyModalOpen(false)}
                className="sm:rounded-2xl"
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default memo(Header)